import styled from '@emotion/styled'
import { Site } from '@pubstack/common/src/site'
import { FunctionComponent, useCallback, useEffect, useState } from 'react'
import { FieldErrors, useForm, useWatch } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useUpdateSite } from '~/api/app/sites.api'
import { Fonts } from '~/assets/style/fonts'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import { useNodeStaffAccess } from '~/hooks/useNodeStaffAccess'
import { useScope } from '~/hooks/useScope'
import { ConnectToPubstack } from '~/feature-components/connect/ConnectToPubstack'
import Button from '~/components/Button'
import { CopyButton } from '~/components/CopyButton'
import { Flyout } from '~/components/Flyout'
import { Input } from '~/components/Input'
import { Tooltip } from '~/components/Tooltip'
import { CardTabs, TabData } from '~/components/CardTabs'
import { TrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { useToast } from '~/components/Toast/useToasts'
import { PATH_BY_PRODUCT, useProduct } from '~/hooks/useProduct'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { hasPbjsVariableChanged, PBJS_VARIABLE_WILL_CHANGE, PBJS_VARIABLE_WILL_CHANGE_TITLE, pbjsVariableLockOf, pbjsVariableNamePayload, SitePbjsVariableInput } from './SitePbjsVariableInput'
import { UPGRADE_OFFER_FEATURE } from '@pubstack/common/src/upgrade'
import { SiteProductPicker } from './SiteProductPicker'
import { SaveConfirmationChallenge, SiteSaveConfirmationModal } from './SiteSaveConfirmationModal'
import { isActivatingProduct, PRODUCT_IS_FINAL, PRODUCT_IS_FINAL_TITLE, SiteProduct, siteProductOf, siteProductPayload } from './siteProduct'

export const MEDIA_NAME_IS_READ_ONLY = 'Only scope owners can rename a media.'

const EditSiteWrapper = styled.div`
  ${Fonts.P1}

  display: flex;
  flex-direction: column;
  gap: 2rem;
`

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  & > p {
    margin: 0;
  }
`

/** `CardTabs` already insets its content by 1rem, so the fields stop 3rem short of it to keep the original 4rem gutter. */
const SectionFields = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding-right: 3rem;
`

const InputIdWrapper = styled.div`
  display: flex;
  gap: 12px;

  & > div {
    flex-grow: 1;
  }
`

const EditActionsWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 24px;
`
const TRACKING_LABEL = 'pbs-edit-site'

type EditSiteFormValues = Site & { product: SiteProduct }

type Props = {
  site: Site
  scopeId: string
  onClose: () => void
}

export const EditSiteFlyout: FunctionComponent<Props> = ({ site, scopeId, onClose }) => {
  // Hooks
  const [nameError, setNameError] = useState<string>('')
  const [activeTab, setActiveTab] = useState(0)
  const navigate = useNavigate()

  const { product } = useProduct()
  const modal = useGlobalModal()
  const user = useAuthenticatedUser()
  // Enabling a product is one-way, so it stays an owner's call. Re-pointing the Prebid variable can
  // cut a media's data collection, so it answers to the same gate.
  const canEnableProduct = user.getScopeRole() === 'owner' || user.isAdmin
  // Renaming answers to the same gate. The name is what every dashboard, alert and export calls this
  // media, so it is not a `user`'s to change under the rest of the scope.
  const canRenameMedia = canEnableProduct
  const scope = useScope()
  const scopeHasOne = scope.hasFeature(UPGRADE_OFFER_FEATURE.one)
  const { hasNode: scopeHasNode, isStaffOnNode } = useNodeStaffAccess()
  const currentProduct = siteProductOf(site)
  const currentPbjsVariableName = site.pbjsVariableName ?? ''

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState: { errors, isValid, isDirty },
  } = useForm<EditSiteFormValues>({
    defaultValues: { ...site, pbjsVariableName: currentPbjsVariableName, product: currentProduct },
    mode: 'onChange',
  })

  const selectedProduct = useWatch({ control, name: 'product' })
  // One runs its own Prebid build under `kleanadsPbjs`, so on a One media the variable is ad management's
  // to set and nobody's to rename — not an owner's, not a staff member's. The field is equally shut while
  // One is being picked in THIS save: a value typed alongside would be overwritten at activation, so it
  // must not be typed (nor sent).
  const isActivatingOne = currentProduct === 'analytics' && selectedProduct === 'adm'
  const pbjsVariableLock = pbjsVariableLockOf({ isManagedByOne: currentProduct === 'adm' || isActivatingOne, isOwner: canEnableProduct })
  const canEditPbjsVariableName = !pbjsVariableLock
  useEffect(() => {
    if (isActivatingOne) setValue('pbjsVariableName', currentPbjsVariableName)
  }, [isActivatingOne, currentPbjsVariableName, setValue])

  const siteName = useWatch({ control, name: 'name' })
  const toast = useToast()

  const { mutateAsync: updateSite, isPending: isSiteUpdatePending } = useUpdateSite(scopeId, site.id, {
    successToast: (
      <>
        <strong>{siteName}</strong> was successfully updated.
      </>
    ),
  })

  const handleUpdateSite = async () => {
    if (!scopeId || !site || isSiteUpdatePending) return

    try {
      const siteName = getValues('name').trim()
      if (!siteName) return

      // The media minus its stored Prebid variable: the route returns the field now, and spreading it back
      // would put the key in every save — which is the one thing `pbjsVariableNamePayload` exists to avoid.
      const { pbjsVariableName: _stored, ...siteWithoutVariable } = site
      const updatedSite: Site = {
        ...siteWithoutVariable,
        // The disabled field is the affordance; keeping the stored name is the invariant.
        name: canRenameMedia ? siteName : site.name,
        // Read from the form, not from `site`: the spread above would otherwise drop the edit.
        ...pbjsVariableNamePayload(getValues('pbjsVariableName'), currentPbjsVariableName, canEditPbjsVariableName),
        // Only an Analytics media can gain a product, and only once.
        ...(currentProduct === 'analytics' ? siteProductPayload(getValues('product')) : {}),
      }

      await updateSite(updatedSite)

      onClose()
    } catch (errors) {
      if (errors.response?.data?.message === 'Conflict data') {
        setNameError('A media with this name already exists.')
      } else {
        setNameError('')
        toast.alert(errors.message)
      }
    }
  }

  /**
   * What this save does that cannot be undone from here, read off the form rather than off `site`.
   *
   * Both can land in the same save — activating Node while re-pointing the variable — so they are
   * collected rather than returned one at a time. Activating One is not one of those pairs: it locks
   * the variable field, so it can only ever raise the product half.
   */
  const pendingConfirmations = (): SaveConfirmationChallenge[] => {
    const challenges: SaveConfirmationChallenge[] = []
    // The name as typed, not as stored: it is what the field behind shows, including a rename saved alongside.
    const typedName = getValues('name').trim()
    if (isActivatingProduct(currentProduct, getValues('product'))) {
      challenges.push({ key: 'product', title: PRODUCT_IS_FINAL_TITLE, description: PRODUCT_IS_FINAL, phrase: typedName })
    }
    const typedVariable = getValues('pbjsVariableName')?.trim() ?? ''
    if (hasPbjsVariableChanged(typedVariable, currentPbjsVariableName, canEditPbjsVariableName)) {
      challenges.push({ key: 'pbjsVariable', title: PBJS_VARIABLE_WILL_CHANGE_TITLE, description: PBJS_VARIABLE_WILL_CHANGE, phrase: typedVariable })
    }
    return challenges
  }

  const handleSave = async () => {
    const challenges = pendingConfirmations()
    if (challenges.length === 0) {
      await handleUpdateSite()
      return
    }
    modal.open(SiteSaveConfirmationModal, {
      title: 'Save changes',
      challenges,
      confirmLabel: 'Save changes',
      trackingLabel: `${product}-edit-site-save-confirm`,
      onConfirm: handleUpdateSite,
    })
  }

  const onCopyToClipboard = (label: string) => {
    toast.success(
      <>
        <strong>{label}</strong> was successfully copied to your clipboard.
      </>
    )
  }

  const getErrorMessage = useCallback(
    (errors: FieldErrors<Site>) => {
      if (errors?.name?.type === 'required') {
        return 'Adapter name is required'
      }

      if (errors?.name?.type === 'pattern') {
        return 'An adapter name cannot only be whitespaces'
      }

      if (nameError) return nameError
    },
    [nameError]
  )

  // The flyout opens from /pbs and /adm alike, so its tabs report against whichever product the user is in.
  const CONFIGURATION_TAB_TRACKING_LABEL: TrackingLabel = `${product}-edit-site-configuration-tab-apply`
  const PRODUCT_TAB_TRACKING_LABEL: TrackingLabel = `${product}-edit-site-product-tab-apply`
  const CONNECT_TAB_TRACKING_LABEL: TrackingLabel = `${product}-edit-site-connect-tab-apply`

  /**
   * The media's own pages, or nothing: an Analytics media has no product tree to open, which is what the
   * list already says by leaving its name as text rather than a link. Built from the media's own product,
   * not from the page the flyout was opened on — one list holds both products.
   */
  const sitePath = currentProduct === 'analytics' ? undefined : `${PATH_BY_PRODUCT[currentProduct]}/sites/${site.id}/configuration`

  /** Each label already names its tab, so the panels carry no heading of their own. */
  const tabs: (TabData & { hasFormActions: boolean })[] = [
    {
      label: 'Configuration',
      hasFormActions: true,
      trackingLabel: CONFIGURATION_TAB_TRACKING_LABEL,
      content: (
        <Section>
          <SectionFields>
            {/* A disabled field dispatches no mouse events, so the padlock — which is not disabled — is what
                the reader can actually hover to find out why the field is shut. */}
            <Tooltip title={MEDIA_NAME_IS_READ_ONLY} disabled={canRenameMedia}>
              <Input
                error={getErrorMessage(errors)}
                onChange={() => setNameError('')}
                control={control}
                name={'name'}
                label={'Media name'}
                readOnly={!canRenameMedia}
                disabled={!canRenameMedia}
                iconRight={canRenameMedia ? undefined : 'lock'}
                rules={canRenameMedia ? { required: true, pattern: /.*[^ ].*/ } : {}}
              />
            </Tooltip>
            <InputIdWrapper>
              <Input readOnly disabled control={control} name={'id'} label={'Media ID'} />
              <CopyButton onCopy={() => onCopyToClipboard('Media ID')} contentText={site.id} trackingLabel={'common-settings-sites-site-id-copy'} />
            </InputIdWrapper>
            <SitePbjsVariableInput control={control} name={'pbjsVariableName'} currentValue={currentPbjsVariableName} lock={pbjsVariableLock} />
          </SectionFields>
        </Section>
      ),
    },
    {
      label: 'Product',
      hasFormActions: true,
      trackingLabel: PRODUCT_TAB_TRACKING_LABEL,
      content: (
        <Section>
          <SiteProductPicker
            control={control}
            name={'product'}
            currentProduct={currentProduct}
            staffOnlyProducts={isStaffOnNode ? ['pbs'] : []}
            availability={{ env: site.env, scopeHasOne, scopeHasNode, canEnableProduct }}
            trackingLabelOf={(p: SiteProduct) => `common-edit-media-product-${p}-set`}
          />
        </Section>
      ),
    },
    {
      label: 'Connect to Pubstack',
      hasFormActions: false,
      trackingLabel: CONNECT_TAB_TRACKING_LABEL,
      content: (
        <Section>
          {/* The activation route is gated on `pbsStandalone`, and this flyout opens over One's media list
              too, so a scope without Node is offered no button rather than one that bounces. */}
          <ConnectToPubstack site={site} trackingPrefix={TRACKING_LABEL} isFolding={false} onActivate={scopeHasNode ? () => navigate(`/monetization/node/sites/${site.id}/activation`) : undefined} />
        </Section>
      ),
    },
  ]

  const isEditableTab = tabs[activeTab]?.hasFormActions ?? false

  return (
    <Flyout.Content>
      <Flyout.Title>{site?.name}</Flyout.Title>
      <Flyout.Body>
        <EditSiteWrapper>
          {/* Configuration for every product alike. The flyout is now what a row click opens, so it has to
              start on the same thing whatever was clicked — an Analytics media landing on Product while its
              neighbours landed on Configuration made one list behave like two. Product is one tab away. */}
          <CardTabs tabs={tabs} initialIndex={0} onTabChange={setActiveTab} />
        </EditSiteWrapper>
      </Flyout.Body>
      <Flyout.Actions>
        <EditActionsWrapper>
          {/* Save and Restore belong to the tabs that hold a field. Connect to Pubstack has none — it is
              code to copy — and a Save under it only ever meant "save what the other tab has open", which
              is not something a reader can see from there. */}
          {isEditableTab && (
            <>
              <Button
                disabled={!isDirty}
                onClick={() => {
                  reset({ ...site, pbjsVariableName: currentPbjsVariableName, product: currentProduct })
                  setNameError('')
                }}
                iconName={'restore'}
                variant={'tertiary'}
                trackingLabel={'pbs-edit-site-restore'}
              >
                Restore
              </Button>
              <Button disabled={!isDirty || !isValid} onClick={handleSubmit(handleSave)} trackingLabel={'pbs-edit-site-save'}>
                {isSiteUpdatePending ? 'Saving changes...' : 'Save changes'}
              </Button>
            </>
          )}
          {/* The way out of the flyout and into the media's own pages, on every tab: what the flyout holds
              is the quick correction, and everything larger — stacks, activation — lives there. An
              Analytics media has no such pages, which is why it gets no button rather than a dead one. */}
          {sitePath && (
            <Button iconName={'edit'} variant={'tertiary'} onClick={() => navigate(sitePath)} trackingLabel={`${product}-edit-site-media-navigate`}>
              Edit
            </Button>
          )}
        </EditActionsWrapper>
      </Flyout.Actions>
    </Flyout.Content>
  )
}
